import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../lib/routes';

const OG_LOCALE_MAP = {
    tr: 'tr_TR',
    en: 'en_US',
    de: 'de_DE'
};

const OG_IMAGE_WIDTH = '1200';
const OG_IMAGE_HEIGHT = '630';
/* The site's 1200 x 630 social image, for pages without their own. */
const DEFAULT_OG_IMAGE = '/og-image.jpg';

/**
 * A page's head through Helmet: title, description, keywords, robots, Open
 * Graph, Twitter and JSON-LD — one of each (index.html carries none of them;
 * the canonical is CanonicalManager's). `exactTitle` is used verbatim; a
 * `title` gets the " | Ankaplastik" suffix.
 */
export default function SEO({
    title,
    exactTitle,
    description,
    url,
    lang = 'tr',
    keywords,
    ogImage,
    ogType = 'website',
    noindex = false,
    jsonLd
}) {
    const fullUrl = `${SITE_URL}${url || '/'}`;
    const defaultTitle = 'Ankaplastik - Endüstriyel Baskı Çözümleri';
    const defaultDesc = 'Tampon baskı, serigrafi ve montaj hizmetleri. Otomotiv yan sanayi için yüksek kaliteli üretim.';

    const metaTitle = exactTitle ?? (title ? `${title} | Ankaplastik` : defaultTitle);
    const metaDesc = description || defaultDesc;
    const ogLocale = OG_LOCALE_MAP[lang] || OG_LOCALE_MAP.tr;
    const image = ogImage || DEFAULT_OG_IMAGE;
    const absoluteOgImage = image.startsWith('http') ? image : `${SITE_URL}${image}`;
    // Only the default image is known to be 1200 x 630; another declares no size.
    const knownSize = image === DEFAULT_OG_IMAGE;
    const jsonLdBlocks = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);

    return (
        <Helmet>
            {/* Basic (<html lang> is set per route by DocumentLang in Router.jsx) */}
            <title>{metaTitle}</title>
            <meta name="description" content={metaDesc} />
            {keywords && <meta name="keywords" content={keywords} />}
            {noindex && <meta name="robots" content="noindex,follow" />}
            {/* The canonical is CanonicalManager's (one per route, from Router). */}

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={ogType} />
            <meta property="og:url" content={fullUrl} />
            <meta property="og:title" content={metaTitle} />
            <meta property="og:description" content={metaDesc} />
            <meta property="og:locale" content={ogLocale} />
            <meta property="og:site_name" content="Ankaplastik" />
            <meta property="og:image" content={absoluteOgImage} />
            {knownSize && <meta property="og:image:width" content={OG_IMAGE_WIDTH} />}
            {knownSize && <meta property="og:image:height" content={OG_IMAGE_HEIGHT} />}

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={fullUrl} />
            <meta name="twitter:title" content={metaTitle} />
            <meta name="twitter:description" content={metaDesc} />
            <meta name="twitter:image" content={absoluteOgImage} />

            {/* Structured data */}
            {jsonLdBlocks.map((block, blockIndex) => (
                <script key={blockIndex} type="application/ld+json">
                    {JSON.stringify(block)}
                </script>
            ))}
        </Helmet>
    );
}
